import { useStyles } from "./InventoryPurchasePopup.style";
import {
  Dialog,
  Typography,
  TypographyVariant,
  Image,
  Icon,
  GemNumber,
} from "@onmo/ui";
import {
  Inventory,
  InventoryAvatarData,
  InventoryBundleData,
  InventoryStatus,
} from "@onmo/graphql/graphql";
import Avatar from "../../core/Avatar/Avatar";
import { useFetchBundlesInventory } from "../../../hooks/resolvers/useShopQuery";
import { usePurchaseInventory } from "../../../hooks/resolvers/useShopMutation";
import { setOpenBundle, setSelectedInventory } from "../../../slices/shop";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { PopupType } from "../../../constants/popup";
import { removePopupQueue } from "../../../slices/popup";
import { useTranslation } from "react-i18next";

const InventoryPurchasePopup: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { selectedInventory } = useAppSelector((state) => state.shop);
  const { currentPopupId } = useAppSelector((state) => state.popup);

  const inventoryAvatar = selectedInventory?.data as InventoryAvatarData;
  const { bundlesInventory } = useFetchBundlesInventory();
  const currentBundle = bundlesInventory?.find((bundle: Inventory) => {
    return (bundle.data as InventoryBundleData).items.includes(
      selectedInventory?.id,
    );
  });
  const purchaseInventory = usePurchaseInventory();
  const { t } = useTranslation();

  const onPurchaseInventory = () => {
    if (!selectedInventory) return;
    purchaseInventory(selectedInventory, true);
  };

  const onOpenBundle = () => {
    if (!currentBundle) return;
    dispatch(setOpenBundle({ openBundle: currentBundle }));
    dispatch(setSelectedInventory({ selectedInventory: undefined }));
    dispatch(removePopupQueue({ type: PopupType.PurchaseInventory }));
  };

  const getMainAction = () => {
    if (!selectedInventory) return;

    if (selectedInventory.status === InventoryStatus.Restricted)
      return {
        text: t("view_bundle"),
        onClick: onOpenBundle,
      };
    if (!selectedInventory.costs?.length) {
      return {
        text: t("claim"),
        onClick: onPurchaseInventory,
      };
    } else
      return {
        text: (
          <>
            {t("buy")} <GemNumber amount={selectedInventory.costs[0].amount} />
          </>
        ),
        onClick: onPurchaseInventory,
      };
  };

  const onClose = () => {
    dispatch(setSelectedInventory({ selectedInventory: undefined }));
    dispatch(removePopupQueue({ type: PopupType.PurchaseInventory }));
  };

  return (
    <Dialog
      open={currentPopupId === PopupType.PurchaseInventory}
      mainAction={getMainAction()}
      secondAction={{
        text: t("close"),
        onClick: onClose,
      }}
      icon={<Avatar size="xl" url={inventoryAvatar?.url} />}
      title={t("avatar")}
    >
      <div className={classes.bundleContent}>
        <div className={classes.bundleInfo}>
          <Image
            src={(currentBundle?.data as InventoryBundleData)?.previewUrl}
            width={40}
            height={40}
            radius={8}
          />
          <div className={classes.info}>
            <Typography variant={TypographyVariant.BodyB5Regular}>
              {t("buy_bundle")}
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB5Bold}
              className={classes.bundleName}
            >
              {currentBundle?.title}
            </Typography>
          </div>
        </div>
        <Icon
          name="icon-box-add"
          className={classes.icon}
          onClick={onOpenBundle}
        />
      </div>
    </Dialog>
  );
};
export default InventoryPurchasePopup;
